import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function adminHeaders(json = false) {
  const h = { Authorization: `Bearer ${localStorage.getItem('hrv_admin_token')}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const FEATURE_FLAGS = [
  { key: 'ai_encyclopedia_enabled',      label: 'AI Test Encyclopedia',    desc: 'AI-powered explanations of lab results' },
  { key: 'ai_drug_interactions_enabled', label: 'Drug Interaction Checker', desc: 'AI checks for drug interactions' },
  { key: 'ai_health_insights_enabled',   label: 'Health Insights',          desc: 'AI-generated health summaries on dashboard' },
  { key: 'ai_document_ocr_enabled',      label: 'Document AI Extraction',   desc: 'AI reads and extracts data from uploaded documents' },
];

export default function AdminSettings() {
  const adminInfo = JSON.parse(localStorage.getItem('hrv_admin_info') || '{}');

  // Password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg]         = useState('');
  const [pwErr, setPwErr]         = useState('');
  const [pwSaving, setPwSaving]   = useState(false);

  // Feature flags + maintenance
  const [settings, setSettings]       = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [flagSaving, setFlagSaving]   = useState({});
  const [flagMsg, setFlagMsg]         = useState('');
  const [showMaintConfirm, setShowMaintConfirm] = useState(false);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/settings`, { headers: adminHeaders() });
      const d = await res.json();
      setSettings(d.settings || {});
    } catch (e) {
      console.error(e);
    } finally {
      setSettingsLoaded(true);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(''); setPwErr('');
    if (newPw !== confirmPw) { setPwErr('New passwords do not match'); return; }
    if (newPw.length < 6)   { setPwErr('New password must be at least 6 characters'); return; }
    setPwSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/change-password`, {
        method: 'POST', headers: adminHeaders(true),
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setPwMsg(d.message);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      setPwErr(e.message || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const updateSetting = async (key, value) => {
    setFlagSaving(prev => ({ ...prev, [key]: true }));
    setFlagMsg('');
    try {
      const res = await fetch(`${API_URL}/admin/settings`, {
        method: 'PUT', headers: adminHeaders(true),
        body: JSON.stringify({ [key]: value }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      setSettings(prev => ({ ...prev, ...d.settings }));
      setFlagMsg('Settings saved');
      setTimeout(() => setFlagMsg(''), 2000);
    } catch (e) {
      setFlagMsg('Error: ' + e.message);
    } finally {
      setFlagSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleFlagToggle = (key, currentValue) => {
    const newVal = currentValue === 'true' ? 'false' : 'true';
    updateSetting(key, newVal);
  };

  const handleMaintenanceToggle = () => {
    if (settings.maintenance_mode === 'false' || !settings.maintenance_mode) {
      setShowMaintConfirm(true);
    } else {
      updateSetting('maintenance_mode', 'false');
    }
  };

  const confirmMaintenance = () => {
    setShowMaintConfirm(false);
    updateSetting('maintenance_mode', 'true');
  };

  const isEnabled = (key) => settings[key] !== 'false';
  const isMaintenance = settings.maintenance_mode === 'true';

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#1a202c', margin: 0 }}>Settings</h1>
        <p style={{ color: '#718096', margin: '6px 0 0' }}>Admin panel configuration and system controls</p>
      </div>

      {/* Maintenance confirm dialog */}
      {showMaintConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '420px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 12px', fontSize: '1.1rem', fontWeight: '700', color: '#1a202c', textAlign: 'center' }}>Enable Maintenance Mode?</h3>
            <p style={{ color: '#718096', textAlign: 'center', margin: '0 0 24px', fontSize: '0.875rem' }}>
              This will block ALL user API requests (login, data fetch, uploads) until you turn it off.
              Only the admin panel will remain accessible.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowMaintConfirm(false)} style={{ flex: 1, padding: '11px', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', color: '#4a5568' }}>
                Cancel
              </button>
              <button onClick={confirmMaintenance} style={{ flex: 1, padding: '11px', background: '#c53030', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>
                Yes, Enable
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Maintenance mode */}
        <div style={{
          background: isMaintenance ? '#fff5f5' : 'white',
          borderRadius: '12px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          border: isMaintenance ? '2px solid #fc8181' : '2px solid transparent',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: '700', color: isMaintenance ? '#c53030' : '#2d3748' }}>
                🔧 Maintenance Mode {isMaintenance && <span style={{ fontSize: '0.78rem', background: '#c53030', color: 'white', padding: '2px 8px', borderRadius: '12px', marginLeft: '8px', verticalAlign: 'middle' }}>ACTIVE</span>}
              </h3>
              <p style={{ margin: 0, color: '#718096', fontSize: '0.85rem' }}>
                Blocks all user API requests. Admin panel stays accessible. Use during upgrades or incidents.
              </p>
              {isMaintenance && (
                <p style={{ margin: '8px 0 0', color: '#c53030', fontSize: '0.8rem', fontWeight: '600' }}>
                  Users are currently seeing: "{settings.maintenance_message}"
                </p>
              )}
            </div>
            <div>
              <div onClick={!settingsLoaded ? undefined : handleMaintenanceToggle} style={{
                width: '52px', height: '28px', borderRadius: '14px', position: 'relative', cursor: 'pointer',
                background: isMaintenance ? '#c53030' : '#e2e8f0', transition: 'background 0.2s',
                flexShrink: 0,
              }}>
                <div style={{
                  position: 'absolute', top: '3px',
                  left: isMaintenance ? '27px' : '3px',
                  width: '22px', height: '22px', borderRadius: '50%', background: 'white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                }} />
              </div>
            </div>
          </div>
          {settingsLoaded && (
            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>Maintenance Message</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  defaultValue={settings.maintenance_message || ''}
                  id="maint-msg-input"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}
                />
                <button onClick={() => {
                  const val = document.getElementById('maint-msg-input').value;
                  updateSetting('maintenance_message', val);
                }} style={{ padding: '8px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  Save Message
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Feature flags */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#2d3748' }}>🤖 AI Feature Flags</h3>
            {flagMsg && (
              <span style={{ fontSize: '0.8rem', color: flagMsg.startsWith('Error') ? '#c53030' : '#276749', fontWeight: '600' }}>
                {flagMsg}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {FEATURE_FLAGS.map(flag => {
              const enabled = isEnabled(flag.key);
              const saving = flagSaving[flag.key];
              return (
                <div key={flag.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#f7fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.875rem' }}>{flag.label}</div>
                    <div style={{ color: '#718096', fontSize: '0.78rem', marginTop: '2px' }}>{flag.desc}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: enabled ? '#276749' : '#c53030' }}>
                      {saving ? '…' : (enabled ? 'ON' : 'OFF')}
                    </span>
                    <div onClick={!saving && settingsLoaded ? () => handleFlagToggle(flag.key, settings[flag.key] || 'true') : undefined}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
                        cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
                        background: enabled ? '#10b981' : '#e2e8f0', transition: 'background 0.2s',
                        flexShrink: 0,
                      }}>
                      <div style={{
                        position: 'absolute', top: '2px',
                        left: enabled ? '22px' : '2px',
                        width: '20px', height: '20px', borderRadius: '50%', background: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                      }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '20px' }}>
          {/* Account info */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1rem', fontWeight: '700', color: '#2d3748' }}>Account</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[['Name', adminInfo.name || '—'], ['Email', adminInfo.email || '—'], ['Role', adminInfo.role || '—']].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f7fafc', borderRadius: '8px' }}>
                  <span style={{ color: '#718096', fontSize: '0.875rem' }}>{k}</span>
                  <span style={{ color: '#2d3748', fontWeight: '600', fontSize: '0.875rem' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Change password */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1rem', fontWeight: '700', color: '#2d3748' }}>Change Password</h3>
            <div style={{ background: '#fffbeb', border: '1px solid #fbd38d', borderRadius: '8px', padding: '10px 14px', marginBottom: '20px', fontSize: '0.8rem', color: '#744210' }}>
              ⚠️ Password changes reset on server restart. Update <code>.env.production</code> to persist.
            </div>
            {pwMsg && <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.875rem' }}>{pwMsg}</div>}
            {pwErr && <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', color: '#c53030', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.875rem' }}>{pwErr}</div>}
            <form onSubmit={handleChangePassword}>
              {[
                ['Current Password', currentPw, setCurrentPw],
                ['New Password', newPw, setNewPw],
                ['Confirm New Password', confirmPw, setConfirmPw],
              ].map(([label, val, setter]) => (
                <div key={label} style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>{label}</label>
                  <input type="password" value={val} onChange={e => setter(e.target.value)} required
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                </div>
              ))}
              <button type="submit" disabled={pwSaving} style={{ padding: '10px 24px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>
                {pwSaving ? 'Saving…' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
